import Component from "@glimmer/component";
import { untrack } from "@glimmer/validator";
import { htmlSafe, isHTMLSafe } from "@ember/template";
import { TrackedArray } from "@ember-compat/tracked-built-ins";
import helperFn from "discourse/helpers/helper-fn";
import deprecated from "discourse/lib/deprecated";
import { isProduction, isRailsTesting, isTesting } from "discourse/lib/environment";
import { WIDGET_DECOMMISSION_OPTIONS } from "discourse/widgets/widget";
import { setComponentTemplate } from "@ember/component";
import { createTemplateFactory } from "@ember/template-factory";
const detachedDocument = document.implementation.createHTMLDocument("detached");
/** @type {Symbol} Default decorator type used when no specific type is provided */
export const NON_STREAM_HTML_DECORATOR = Symbol("non-stream");
/** Helper object for contexts without a post/model. Use with `applyHtmlDecorators`. */
export const NULL_HELPER = Object.freeze({
  getModel: () => null,
  get model() {
    return null;
  },
  get context() {
    return null;
  }
});
/** @type {Object.<Symbol|string, Function[]>} Storage for HTML decorators organized by type */
let htmlDecorators = {};
/**
 * Gets or initializes an array of HTML decorators for a specific type
 *
 * @param {Symbol|string} [type] - The decorator type to get decorators for. If not provided,
 *                                defaults to NON_STREAM_HTML_DECORATOR
 * @returns {Function[]} An array of decorator functions for the specified type
 * @example
 * const decorators = getHtmlDecoratorsForType(myDecoratorType);
 * // Returns existing decorators array or initializes a new one
 */
function getHtmlDecoratorsForType(type) {
  return htmlDecorators[type ?? NON_STREAM_HTML_DECORATOR] ||= [];
}
/**
 * Registers a new HTML decorator function for a specific type of decoration.
 *
 * @param {Function} decorator - The decorator function to register. It receives two parameters:
 *                              1. element: HTMLElement to be decorated
 *                              2. options: Object containing decoration options
 *                              The decorator can optionally return a cleanup function.
 * @param {Symbol|string} [type=NON_STREAM_HTML_DECORATOR] - The type of decorator to register.
 *                                                       When not provided, defaults to NON_STREAM_HTML_DECORATOR (non-stream).
 * @returns {void}
 * @example
 * registerHtmlDecorator((element, options) => {
 *   element.classList.add('decorated');
 *   return () => element.classList.remove('decorated'); // Cleanup function
 * });
 */
export function registerHtmlDecorator(decorator, type) {
  getHtmlDecoratorsForType(type).push(decorator);
}
/**
 * Applies registered HTML decorators to a DOM element and returns their cleanup functions.
 *
 * @param {HTMLElement} element - The DOM element to apply decorators to
 * @param {Object} helper - Helper object passed to each decorator. Use NULL_HELPER for contexts without a model.
 * @param {Symbol|string} [type=NON_STREAM_HTML_DECORATOR] - The type of decorators to apply.
 * @returns {Function[]} Array of cleanup functions from decorators that returned them.
 */
export function applyHtmlDecorators(element, helper, type) {
  return getHtmlDecoratorsForType(type).map(decorator => {
    return decorator(element, helper);
  }).filter(fn => typeof fn === "function");
}
/**
 * Clears all registered HTML decorators
 *
 * USE ONLY FOR TESTING PURPOSES
 *
 * @returns {void}
 */
export function resetHtmlDecorators() {
  if (isTesting()) {
    htmlDecorators = {};
  }
}
/**
 * Reactively renders cooked HTML with decorations applied.
 *
 * When no custom `@decorate` function is provided, registered HTML decorators
 * (via `api.decorateCookedElement`) are automatically applied. This handles
 * common cases like hashtag icons, mentions, and other cooked content decorations.
 *
 * @component DecoratedHtml
 * @param {SafeString} html - The HTML content to render (must be wrapped with htmlSafe)
 * @param {Function} [decorate] - Custom decorator function receiving (element, helper, decorateArgs).
 *                                When provided, you're responsible for calling applyHtmlDecorators if needed.
 * @param {Object} [decorateArgs] - Additional arguments passed to the decorate function
 * @param {Object} [model] - Model object (e.g., post) passed to decorators via helper.model
 * @param {Object} [context] - Context object passed to decorators via helper.context
 * @param {string} [className] - CSS class name for the wrapper div
 * @param {string} [id] - ID attribute for the wrapper div
 */
export default class DecoratedHtml extends Component {
  renderGlimmerInfos = new TrackedArray();
  decoratedContent = helperFn(({
    decorateArgs
  }, on) => {
    const cookedDiv = this.elementToDecorate;
    const helper = new DecorateHtmlHelper({
      renderGlimmerInfos: this.renderGlimmerInfos,
      model: this.args.model,
      context: this.args.context
    });
    on.cleanup(() => helper.teardown());
    const decorateFn = this.args.decorate;
    // force parameters explicitly declared in `decorateArgs` to be tracked despite the
    // use of `untrack` below
    decorateArgs && Object.values(decorateArgs);
    try {
      untrack(() => {
        if (decorateFn) {
          // Custom decorator provided - it's responsible for calling applyHtmlDecorators if needed
          decorateFn(cookedDiv, helper, decorateArgs);
        } else {
          // No custom decorator - automatically apply registered HTML decorators
          applyHtmlDecorators(cookedDiv, helper);
        }
      });
    } catch (e) {
      if (isRailsTesting() || isTesting()) {
        throw e;
      } else {
        // in case one of the decorators throws an error we want to surface it to the console but prevent
        // the application from crashing
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
    document.adoptNode(cookedDiv);
    return cookedDiv;
  });
  get elementToDecorate() {
    const cooked = this.args.html || htmlSafe("");
    if (!isHTMLSafe(cooked)) {
      throw "@html must be an htmlSafe string";
    }
    const cookedDiv = detachedDocument.createElement("div");
    cookedDiv.innerHTML = cooked.toString();
    if (this.args.id) {
      cookedDiv.id = this.args.id;
    }
    if (this.args.className) {
      cookedDiv.className = this.args.className;
    }
    return cookedDiv;
  }
  /**
  * Checks if a given HTML element belongs to the current document.
  * In development mode, it warns if the element is not in the document.
  *
  * This is used to ensure components added using `renderGlimmer` are only rendered in the same document, preventing
  * rendering errors that otherwise would crash the application.
  *
  * @param {Object} info - Object containing element information
  * @param {Element} info.element - The DOM element to check
  * @returns {boolean} True if element belongs to current document, false otherwise
  */
  isElementInDocument(info) {
    const result = info.element.ownerDocument === document;
    if (!isProduction() && !result) {
      // eslint-disable-next-line no-console
      console.warn("The `renderGlimmer` definition below was unable to render the decorated HTML because the target element is not in the " + "current document. This likely occurred because the element was removed by another decorator.\n", info);
    }
    return result;
  }
  static {
    setComponentTemplate(createTemplateFactory(
    /*
      
        {{~this.decoratedContent decorateArgs=@decorateArgs~}}
    
        {{~#each this.renderGlimmerInfos as |info|~}}
          {{~#if (this.isElementInDocument info)~}}
            {{~#if info.append}}
              {{~#in-element info.element insertBefore=null~}}
                <info.component @data={{info.data}} />
              {{~/in-element~}}
            {{~else}}
              {{~#in-element info.element~}}
                <info.component @data={{info.data}} />
              {{~/in-element~}}
            {{~/if}}
          {{~/if~}}
        {{~/each~}}
      
    */
    {
      "id": "jeGOcbB1",
      "block": "[[[1,[28,[30,0,[\"decoratedContent\"]],null,[[\"decorateArgs\"],[[30,1]]]]],[42,[28,[31,1],[[28,[31,1],[[30,0,[\"renderGlimmerInfos\"]]],null]],null],null,[[[41,[28,[30,0,[\"isElementInDocument\"]],[[30,2]],null],[[[41,[30,2,[\"append\"]],[[[40,[[[8,[30,2,[\"component\"]],null,[[\"@data\"],[[30,2,[\"data\"]]]],null]],[]],\"%cursor:0%\",[28,[31,4],[[30,2,[\"element\"]]],null],null]],[]],[[[40,[[[8,[30,2,[\"component\"]],null,[[\"@data\"],[[30,2,[\"data\"]]]],null]],[]],\"%cursor:1%\",[28,[31,4],[[30,2,[\"element\"]]],null]]],[]]]],[]],null]],[2]],null]],[\"@decorateArgs\",\"info\"],[\"each\",\"-track-array\",\"if\",\"in-element\",\"-in-el-null\"]]",
      "moduleName": "/var/www/discourse/frontend/discourse/node_modules/.embroider/rewritten-app/components/decorated-html.js",
      "isStrictMode": true
    }), this);
  }
}
class DecorateHtmlHelper {
  #renderGlimmerInfos;
  #model;
  #context;
  constructor({
    renderGlimmerInfos,
    model,
    context
  }) {
    this.#renderGlimmerInfos = renderGlimmerInfos;
    this.#model = model;
    this.#context = context;
  }
  /**
  * Renders a Glimmer component into a specified target HTML element with provided data.
  * Handles deprecation warnings if invalid parameters are passed.
  *
  * @param {Element} targetElement The HTML element where the Glimmer component should be rendered. Must be a valid DOM element.
  * @param {Object} component The Glimmer component object to render. Should not have a name of "factory" to avoid deprecation warnings.
  * @param {Object} data The data to be passed to the Glimmer component for rendering.
  * @param {Object} [opts={}] Additional options for rendering. Supports an `append` boolean property that defaults to `true` to determine if the content should be appended to the target element.
  * @return {void}
  */
  renderGlimmer(targetElement, component, data, opts = {}) {
    if (!(targetElement instanceof Element)) {
      deprecated("Invalid `targetElement` passed to `helper.renderGlimmer` while using `api.decorateCookedElement` with the Glimmer Post Stream. `targetElement` must be a valid HTML element. This call has been ignored to prevent errors.", WIDGET_DECOMMISSION_OPTIONS);
      return;
    }
    if (component.name === "factory") {
      deprecated("Invalid `component` passed to `helper.renderGlimmer` while using `api.decorateCookedElement` with the Glimmer Post Stream. `component` must be a valid Glimmer component. If using a template compiled via ember-cli-htmlbars, replace it with the `<template>...</template>` syntax. This call has been ignored to prevent errors.", WIDGET_DECOMMISSION_OPTIONS);
      return;
    }
    const info = {
      element: targetElement,
      component,
      data,
      append: opts.append ?? true
    };
    this.#renderGlimmerInfos.push(info);
  }
  get model() {
    return this.#model;
  }
  get context() {
    return this.#context;
  }
  getModel() {
    return this.model;
  }
  get widget() {
    deprecated("Using `helper.widget` has been decommissioned. See https://meta.discourse.org/t/372063/1", WIDGET_DECOMMISSION_OPTIONS);
  }
  teardown() {
    this.#renderGlimmerInfos.length = 0;
  }
}